import { useTranslation } from 'react-i18next';
import {
  Bar,
  Checkbox,
  HintError,
  Input,
  Pagination,
  Row,
  RowList,
  Stack,
  Switch,
  Typography,
} from '@src/components';

export type ExclusionOption = {
  /** Stable identity when participant names are not unique. */
  id?: string;
  name: string;
  /** This person is on the editor subject's exclusion list. */
  selected: boolean;
  /** ...and the subject is on theirs. */
  mutual: boolean;
};

export interface ExclusionEditorProps {
  options: ExclusionOption[];
  participantCount: number;
  page: number;
  onPageChange: (page: number) => void;
  subtitle: string;
  /** Search only appears for a list long enough to need it. */
  query: string;
  onQueryChange: (query: string) => void;
  searchLabel: string;
  peopleLabel: string;
  mutualLabel: string;
  mutualHint: string;
  /** Set when the current selection makes a draw impossible. */
  error?: string;
  onToggle: (name: string, selected: boolean) => void;
  onToggleMutual: (name: string, mutual: boolean) => void;
  testId?: string;
}

/** A list of names long enough that scanning it beats scrolling it. */
const SEARCH_THRESHOLD = 10;

/**
 * The body of the per-participant exclusion dialog. It keeps no state of its
 * own: the page owns the draft and the search term, so re-showing the dialog
 * after every toggle renders the same thing it would have rendered anyway.
 */
export function ExclusionEditor({
  options,
  participantCount,
  page,
  onPageChange,
  subtitle,
  query,
  onQueryChange,
  searchLabel,
  peopleLabel,
  mutualLabel,
  mutualHint,
  error,
  onToggle,
  onToggleMutual,
  testId,
}: ExclusionEditorProps) {
  const { t } = useTranslation();
  const searchable = participantCount > SEARCH_THRESHOLD;
  const needle = query.trim().toLowerCase();
  const filtered =
    searchable && needle
      ? options.filter((option) => option.name.toLowerCase().includes(needle))
      : options;
  const pageCount = Math.max(1, Math.ceil(filtered.length / 10));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * 10, currentPage * 10);
  const anySelected = options.some((option) => option.selected);

  return (
    <Stack gap={5} testId={testId}>
      <Stack gap={2}>
        <Typography as="p" role="body/sm">
          {subtitle}
        </Typography>
        {anySelected && (
          <Typography as="p" role="body/sm">
            {mutualHint}
          </Typography>
        )}
      </Stack>

      {searchable && (
        <Input
          type="search"
          name="exclusion-search"
          label={searchLabel}
          value={query}
          onValueChange={onQueryChange}
          autoComplete="off"
          testId="exclusion-search"
        />
      )}

      <Stack gap={3}>
        <Typography as="h3" role="label/field">
          {peopleLabel}
        </Typography>
        <RowList testId="exclusion-list">
          {visible.map((option) => (
            <Row
              key={option.id ?? option.name}
              align="center"
              testId={`exclusion-option-${option.id ?? option.name}`}
            >
              <Bar align="center" gap={4} wide>
                <Checkbox
                  fit
                  checked={option.selected}
                  onCheckedChange={(selected) => onToggle(option.id ?? option.name, selected)}
                  label={option.name}
                  testId={`exclusion-choice-${option.id ?? option.name}`}
                />
                {option.selected && (
                  <Switch
                    checked={option.mutual}
                    onCheckedChange={(mutual) => onToggleMutual(option.id ?? option.name, mutual)}
                    label={mutualLabel}
                    testId={`exclusion-mutual-${option.id ?? option.name}`}
                  />
                )}
              </Bar>
            </Row>
          ))}
        </RowList>
        {filtered.length === 0 && <Typography role="body/sm">{t('organizerPanel.exclusionsSearchEmpty')}</Typography>}
        <Pagination page={currentPage} pageCount={pageCount} onChange={onPageChange}
          previousLabel={t('organizerPanel.exclusionsPrevious')}
          nextLabel={t('organizerPanel.exclusionsNext')}
          pageLabel={number => t('organizerPanel.exclusionsPage', { number })}
          testId="exclusion-editor-pagination" />
      </Stack>

      <HintError error={error ?? null} testId="exclusion-error" />
    </Stack>
  );
}
